// src/pages/Auth/Login.jsx (REVISED for Email OTP Only Flow)

import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import logo from "../../assets/logo.jpeg";
// 🚀 FIX 1: Use NAMED IMPORT for loginUser
import { loginUser } from "../../api/auth-api"; 
// 🚀 FIX 2: Correct paths for components (Assuming /Auth folder)
// We still import these components, but the logic will stop us from using them.
import TwoFactorSetup from "../../components/TwoFactorSetup"; 
import TwoFactorPrompt from "../../components/TwoFactorPrompt"; 
import EmailVerificationPrompt from "../../components/EmailVerificationPrompt"; 

// Define the steps of the login process (UPDATED)
const LOGIN_STEP = {
  CREDENTIALS: 'CREDENTIALS',
  PENDING_EMAIL_OTP: 'PENDING_EMAIL_OTP', 
  // We keep the following steps defined, but the flow will skip them for now
  PENDING_2FA_SETUP: 'PENDING_2FA_SETUP', 
  PENDING_2FA_VERIFY: 'PENDING_2FA_VERIFY', 
  LOADING: 'LOADING'
};

const Login = () => {
  const navigate = useNavigate();
  
  const [loginStep, setLoginStep] = useState(LOGIN_STEP.CREDENTIALS);
  const [employeeEmail, setEmployeeEmail] = useState("");
  const [password, setPassword] = useState("");
  const [tempToken, setTempToken] = useState(null); 
  const [userMetadata, setUserMetadata] = useState(null); 
  
  const [showPassword, setShowPassword] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Helper function to handle final redirection after successful login/2FA
  const handleRedirect = (role) => {
    switch (role) {
      case "hr_officer":
        navigate("/hr-dashboard");
        break;
      case "admin":
        navigate("/admin-dashboard");
        break;
      case "program_manager":
        navigate("/program-dashboard");
        break;
      case "program_director":
        navigate("/program-director");
        break;
      case "field_officer":
        navigate("/program-staff");
        break;
      case "operations_associate":
        navigate("/operations-dashboard");
        break;
      case "head_of_operations":
        navigate("/head-of-operations-dashboard");
        break;
      case "finance_officer":
        navigate("/finance-dashboard");
        break;
      case "finance_manager":
        navigate("/finance-manager");
        break;
      default:
        navigate("/staff-dashboard");
    }
  };

  // 🔑 CRITICAL CHANGE: After successful Email OTP verification, we now complete the login
  const handleEmailVerificationSuccess = (res) => {
      // The response 'res' from verifyEmailOTP now contains the final JWT and user data
      
      if (res.token) {
          // 1. Store the final JWT token
          localStorage.setItem("token", res.token);
          localStorage.setItem("user", JSON.stringify(res.user));

          // 2. Complete the login flow
          setSuccess("Email verified and login successful! Redirecting...");
          setLoginStep(LOGIN_STEP.LOADING);
          setTempToken(null); 
          setUserMetadata(null);
          
          setTimeout(() => {
              handleRedirect(res.user.role);
          }, 1000);
          return;
      }

      // Fallback (Should not happen in the new 2FA flow, but good to keep for safety)
      // This is the old 3FA logic:
      setSuccess("Email verified. Proceeding to final security check.");
      setTempToken(res.tempToken); 
      setUserMetadata(res.user);

      if (res.user && res.user.is2FASetupComplete) {
          setLoginStep(LOGIN_STEP.PENDING_2FA_VERIFY);
      } else {
          setLoginStep(LOGIN_STEP.PENDING_2FA_SETUP);
      }
  }


  // Callback from 2FA components to signal final successful authentication (OLD LOGIC)
  // This will still be called if a user somehow ends up in the PENDING_2FA steps
  const handleTfaCodeSubmitted = (userRole) => {
      setSuccess("2FA successful! Redirecting...");
      setLoginStep(LOGIN_STEP.LOADING);
      setTempToken(null); 
      setUserMetadata(null);
      
      setTimeout(() => {
          handleRedirect(userRole);
      }, 1000);
  }
  
  // Callback for when any session expires or is cancelled (returns to credentials)
  const handleTfaLogout = () => {
      setTempToken(null);
      setUserMetadata(null);
      setLoginStep(LOGIN_STEP.CREDENTIALS);
      setError("Login session expired or cancelled. Please log in again.");
  }


  const handleCredentialsSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);

    try {
      const res = await loginUser(employeeEmail, password);

      // ------------------------------------------------------------------
      // Case 1: First-Time Login (Status 202, isFirstTimeLogin: true)
      // ------------------------------------------------------------------
      if (res.isFirstTimeLogin) {
        setSuccess(res.message);
        localStorage.setItem("tempEmailForPasswordChange", res.email);
        localStorage.setItem("tempOldPasswordForPasswordChange", password); 

        setTimeout(() => {
          navigate("/change-password");
        }, 1000);
        return; 
      }
      
      // ------------------------------------------------------------------
      // 🔑 Case 2: EMAIL OTP Required Flow (200 with emailOtpRequired flag)
      // ------------------------------------------------------------------
      if (res.emailOtpRequired && res.tempToken) { 
        setSuccess(res.message); 
        setTempToken(res.tempToken);
        setLoginStep(LOGIN_STEP.PENDING_EMAIL_OTP);
        setLoading(false); 
        return; 
      }
      
      // ------------------------------------------------------------------
      // Case 3 & 4: Fallback to 3FA or Standard Login (Keep for robustness)
      // ------------------------------------------------------------------
      if (res.twoFactorRequired && res.tempToken) { 
        // This is the old 3FA flow. If backend still sends this, we follow it.
        setSuccess(res.message); 
        setTempToken(res.tempToken);
        
        if (res.user.is2FASetupComplete) {
            setLoginStep(LOGIN_STEP.PENDING_2FA_VERIFY);
        } else {
            setLoginStep(LOGIN_STEP.PENDING_2FA_SETUP);
        }
        
        setLoading(false); 
        return; 
      }
      
       if (res.token) {
          // This is a final JWT token (e.g., if MFA was disabled)
          localStorage.setItem("token", res.token);
          localStorage.setItem("user", JSON.stringify(res.user));

          setSuccess("Login successful! Redirecting...");
          setTimeout(() => setSuccess(""), 3000);

          setTimeout(() => {
            handleRedirect(res.user.role);
          }, 1000);
          return;
       }


    } catch (err) {
      console.error("Login failed:", err);
      setError(err.message || "Login failed. Please check your credentials.");
    } finally {
      if (loginStep === LOGIN_STEP.CREDENTIALS) {
        setLoading(false);
      }
    }
  };


  // --- Conditional UI Rendering ---

  // 1. Loading State
  if (loading || loginStep === LOGIN_STEP.LOADING) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">
             {loginStep === LOGIN_STEP.LOADING ? "Finalizing login..." : "Logging in..."}
          </p>
        </div>
      </div>
    );
  }

  // 🔑 2. Email OTP Verification State (The final security step)
  if (loginStep === LOGIN_STEP.PENDING_EMAIL_OTP) {
      return (
        <main className="min-h-screen flex items-center justify-center bg-gray-100">
            <EmailVerificationPrompt 
                tempToken={tempToken} 
                email={employeeEmail} 
                onVerificationSuccess={handleEmailVerificationSuccess} 
                onLogout={handleTfaLogout}
            />
        </main>
      );
  }

  // 3. 2FA Setup State (First Time) - Still defined, but the flow skips it
  if (loginStep === LOGIN_STEP.PENDING_2FA_SETUP) {
      return (
        <main className="min-h-screen flex items-center justify-center bg-gray-100">
            <TwoFactorSetup 
                tempToken={tempToken} 
                onLoginSuccess={handleTfaCodeSubmitted} 
                onClose={handleTfaLogout}
            />
        </main>
      );
  }
  
  // 4. 2FA Verification State (Subsequent Login) - Still defined, but the flow skips it
  if (loginStep === LOGIN_STEP.PENDING_2FA_VERIFY) {
      return (
        <main className="min-h-screen flex items-center justify-center bg-gray-100">
            <TwoFactorPrompt
                tempToken={tempToken} 
                onLoginSuccess={handleTfaCodeSubmitted}
                onLogout={handleTfaLogout}
            />
        </main>
      );
  }


  // 5. Credentials Input State
  const renderCredentialsForm = () => (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <h1 className="text-2xl font-semibold text-center mb-2">Welcome</h1>
      <p className="text-sm text-center text-gray-600 mb-6">
        Hold The Child Data Management System
      </p>

      <form onSubmit={handleCredentialsSubmit} className="space-y-4">
        {success && (
          <div className="flex items-center gap-2 text-green-700 bg-green-100 border border-green-300 text-sm px-4 py-2 rounded-md animate-fadeIn">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
            <span>{success}</span>
          </div>
        )}

        {error && (
          <div className="flex items-center gap-2 text-red-600 bg-red-100 border border-red-300 text-sm px-4 py-2 rounded-md animate-fadeIn duration-300">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12c0-4.97-4.03-9-9-9s-9 4.03-9 9 4.03 9 9 9 9-4.03 9-9z" />
              </svg>
            <span>{error}</span>
          </div>
        )}

        <div>
          <label htmlFor="employeeEmail" className="block text-gray-700 mb-1">
            Email
          </label>
          <input
            id="employeeEmail"
            type="email"
            value={employeeEmail}
            onChange={(e) => setEmployeeEmail(e.target.value)}
            placeholder="Enter your email"
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
        </div>

        <div className="relative">
          <label htmlFor="password" className="block text-gray-700 mb-1">
            Password
          </label>
          <div className="relative">
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
              className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
            <button
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 hover:text-orange-500 focus:outline-none"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.27-2.944-9.544-7a10.048 10.048 0 012.34-4.021m1.873-1.639a10.041 10.041 0 0111.828 2.336M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 3l18 18" />
                </svg>
              )}
            </button>
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-orange-500 text-white py-2 rounded-md hover:bg-orange-600 transition-colors"
        >
          Login
        </button>
      </form>

      <p className="text-xs mt-4 text-center text-gray-600">
        Forgot your password or want to change it?{" "}
        <Link to="/reset-password" className="text-orange-500 font-medium">
          Click here
        </Link>
      </p>
    </div>
  );

  // Final Return: Renders the credential form and surrounding layout
  return (
    <main className="min-h-screen flex items-center justify-center bg-gray-100">
      <section className="w-full max-w-5xl bg-white rounded-md shadow-md flex flex-col md:flex-row overflow-hidden">
        {/* Left Side */}
        <div className="w-full md:w-1/2 p-8">
          <img src={logo} alt="Hold the Child Logo" className="h-12 mb-6" />
          {/* Always render the credentials form when not in 2FA flow */}
          {loginStep === LOGIN_STEP.CREDENTIALS && renderCredentialsForm()}
        </div>

        {/* Right Side */}
        <div className="hidden md:flex w-1/2 bg-white items-center justify-center p-10">
          <blockquote className="text-orange-600 font-semibold text-lg leading-relaxed border-l-4 border-orange-500 pl-4 italic">
            Our commitment to promote the best interest of the Child, amidst
            limited resources has tremendously built our reputation in the
            communities where we work and our partners.
          </blockquote>
        </div>
      </section>
    </main>
  );
};

export default Login;